import { useState, useEffect, useCallback } from 'react';
import { GlassCard, GlassCardContent } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Switch } from '@easyflow/ui-master/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-master/components/ui/dialog';
import { Puzzle, RefreshCw, CheckCircle2, XCircle, Eye, EyeOff, Loader2, MapPin, Facebook } from 'lucide-react';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface Integration {
  id: string;
  integration_type: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface IntegrationDetail extends Integration {
  // here_maps (retro-compat)
  has_api_key?: boolean;
  masked_key?: string;
  // generico per campi non segreti / flag segreti
  [key: string]: unknown;
}

type FieldType = 'text' | 'secret';

interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  help?: string;
}

/** Genera un verify token webhook casuale (32 byte → hex), da incollare nel
 *  campo "Verify Token" del webhook nell'app Meta. */
function generateVerifyToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

const INTEGRATION_META: Record<string, { name: string; description: string; icon: typeof MapPin; fields: FieldDef[] }> = {
  here_maps: {
    name: 'HERE Maps',
    description: 'Autocompletamento indirizzi per CRM e vendite',
    icon: MapPin,
    fields: [{ key: 'api_key', label: 'API Key', type: 'secret', placeholder: 'Inserisci API Key' }],
  },
  meta_app: {
    name: 'Meta App',
    description: 'App Meta centralizzata EasyFlow per connessione Facebook/Instagram dei tenant',
    icon: Facebook,
    fields: [
      { key: 'app_name', label: 'Nome App', type: 'text', placeholder: 'EASYFLOW' },
      { key: 'app_id', label: 'App ID', type: 'text', placeholder: 'ID numerico dell\'app Meta' },
      { key: 'app_secret', label: 'App Secret', type: 'secret', placeholder: 'Secret dell\'app (cifrato)' },
      { key: 'config_id', label: 'Configuration ID', type: 'text', placeholder: 'config_id Facebook Login for Business' },
      {
        key: 'redirect_uri',
        label: 'Redirect URI OAuth',
        type: 'text',
        placeholder: 'https://app.easyflowai.it/api/meta/oauth/callback',
        help: 'Deve essere registrato tra i Valid OAuth Redirect URIs dell\'app Meta',
      },
      { key: 'graph_api_version', label: 'Versione Graph API', type: 'text', placeholder: 'v23.0' },
      {
        key: 'webhook_verify_token',
        label: 'Webhook Verify Token',
        type: 'secret',
        placeholder: 'Stringa segreta inventata da te',
        help: 'Stesso valore da inserire nel campo "Verify Token" del webhook nell\'app Meta. Callback URL: https://app.easyflowai.it/api/webhooks/meta',
      },
      { key: 'client_token', label: 'Client Token (opzionale)', type: 'secret', placeholder: 'Per chiamate app-level (cifrato)' },
    ],
  },
};

export default function IntegrationsMaster() {
  const { toast } = useToast();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [detail, setDetail] = useState<IntegrationDetail | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [enabled, setEnabled] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const loadIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<Integration[]>('/master/integrations');
      if (res.success && res.data) {
        setIntegrations(res.data);
      }
    } catch (err) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : 'Errore nel caricamento', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void loadIntegrations(); }, [loadIntegrations]);

  const fieldsFor = (type: string | null): FieldDef[] => (type ? INTEGRATION_META[type]?.fields ?? [] : []);

  const openModal = async (integrationType: string) => {
    setSelectedType(integrationType);
    setValues({});
    setShowSecret({});
    setTestResult(null);
    setDetail(null);
    setEnabled(false);
    try {
      const res = await apiClient.get<IntegrationDetail>(`/master/integrations/${integrationType}`);
      if (res.success && res.data) {
        const data = res.data;
        setDetail(data);
        setEnabled(data.enabled);
        // Prefill solo dei campi non segreti (i segreti restano vuoti col placeholder mascherato).
        const initial: Record<string, string> = {};
        for (const f of fieldsFor(integrationType)) {
          if (f.type === 'text') {
            const v = data[f.key];
            if (typeof v === 'string') initial[f.key] = v;
          }
        }
        setValues(initial);
      }
    } catch { /* la card resta in stato non configurato */ }
    setModalOpen(true);
  };

  const maskedFor = (key: string): string => {
    if (!detail) return '';
    if (key === 'api_key' && typeof detail.masked_key === 'string') return detail.masked_key;
    const masked = detail[`masked_${key}`];
    return typeof masked === 'string' ? masked : '';
  };

  const hasSecret = (key: string): boolean => {
    if (!detail) return false;
    if (key === 'api_key' && typeof detail.has_api_key === 'boolean') return detail.has_api_key;
    return detail[`has_${key}`] === true;
  };

  const buildPayload = (): Record<string, string> => {
    const payload: Record<string, string> = {};
    for (const f of fieldsFor(selectedType)) {
      const v = (values[f.key] ?? '').trim();
      if (v.length > 0) payload[f.key] = v;
    }
    return payload;
  };

  const handleTest = async () => {
    if (!selectedType) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await apiClient.post<{ message?: string }>(`/master/integrations/${selectedType}/test`, buildPayload());
      if (res.success) {
        setTestResult({ success: true, message: (res.data as { message?: string })?.message || res.message || 'Connessione riuscita' });
      } else {
        setTestResult({ success: false, message: res.error || 'Test fallito' });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      setTestResult({ success: false, message: axiosErr.response?.data?.error || axiosErr.message || 'Errore nel test' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!selectedType) return;
    setSaving(true);
    try {
      const body: Record<string, string | boolean> = { ...buildPayload(), enabled };
      const res = await apiClient.put(`/master/integrations/${selectedType}`, body);
      if (res.success) {
        toast({ title: 'Salvato', description: 'Integrazione aggiornata' });
        setModalOpen(false);
        void loadIntegrations();
      } else {
        toast({ title: 'Errore', description: res.error || 'Errore nel salvataggio', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : 'Errore nel salvataggio', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const selectedMeta = selectedType ? INTEGRATION_META[selectedType] : null;
  const ModalIcon = selectedMeta?.icon || Puzzle;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Puzzle className="h-7 w-7 text-blue-600" />
            Integrazioni Piattaforma
          </h1>
          <p className="text-muted-foreground mt-1">Gestione integrazioni di piattaforma cross-tenant</p>
        </div>
        <Button variant="outline" onClick={loadIntegrations} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {integrations.map((integration) => {
            const meta = INTEGRATION_META[integration.integration_type];
            const Icon = meta?.icon || Puzzle;
            return (
              <GlassCard
                key={integration.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => openModal(integration.integration_type)}
              >
                <GlassCardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <Icon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{meta?.name || integration.integration_type}</h3>
                        <p className="text-sm text-muted-foreground">{meta?.description || ''}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    {integration.enabled ? (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Attivo
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <XCircle className="h-3 w-3 mr-1" /> Non configurato
                      </Badge>
                    )}
                  </div>
                </GlassCardContent>
              </GlassCard>
            );
          })}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ModalIcon className="h-5 w-5 text-blue-600" />
              {selectedMeta?.name || selectedType}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {fieldsFor(selectedType).map((field) => (
              <div key={field.key}>
                <Label>{field.label}</Label>
                {field.type === 'secret' ? (
                  <div className="relative">
                    <Input
                      type={showSecret[field.key] ? 'text' : 'password'}
                      value={values[field.key] ?? ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={hasSecret(field.key) ? (maskedFor(field.key) || '****') : (field.placeholder || '')}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-muted-foreground"
                      onClick={() => setShowSecret((prev) => ({ ...prev, [field.key]: !prev[field.key] }))}
                    >
                      {showSecret[field.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                ) : (
                  <Input
                    type="text"
                    value={values[field.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder || ''}
                  />
                )}
                {field.key === 'webhook_verify_token' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      const token = generateVerifyToken();
                      setValues((prev) => ({ ...prev, [field.key]: token }));
                      setShowSecret((prev) => ({ ...prev, [field.key]: true }));
                    }}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Genera token
                  </Button>
                )}
                {field.help && <p className="text-xs text-muted-foreground mt-1">{field.help}</p>}
                {field.type === 'secret' && hasSecret(field.key) && !(values[field.key] ?? '') && (
                  <p className="text-xs text-muted-foreground mt-1">Già configurato: {maskedFor(field.key)}</p>
                )}
              </div>
            ))}

            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleTest}
                disabled={testing}
                className="w-full"
              >
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Testa Connessione
              </Button>
              {testResult && (
                <div className={`mt-2 p-2 rounded text-sm ${testResult.success ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
                  {testResult.success ? <CheckCircle2 className="h-4 w-4 inline mr-1" /> : <XCircle className="h-4 w-4 inline mr-1" />}
                  {testResult.message}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label>Attiva integrazione</Label>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
